import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { bookingsService, auditService, invoiceService, nightAuditService } from '../services/api';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import RoomManagement from './RoomManagement';
import PropertyRoomTypeManager from './PropertyRoomTypeManager';
import CourseManagement from './CourseManagement';
import BookingCalendar from './BookingCalendar';
import AvailabilityCalendar from './AvailabilityCalendar';
import PriceManagement from './PriceManagement';
import StaffReservation from './StaffReservation';
import UserManagementNew from './UserManagementNew';
import AddOnManagement from './AddOnManagement';
import ItemsManagement from './ItemsManagement';
import ServicesBooking from './ServicesBooking';
import Reports from './Reports';
import NewReports from './NewReports';
import CustomReports from './CustomReports';
import ProfileModal from './modals/ProfileModal';
import EditBookingModal from './modals/EditBookingModal';
import InvoiceSelectionModal from './modals/InvoiceSelectionModal';
import PaymentLog from './PaymentLog';
import ImageDebugger from './ImageDebugger';
import NightAudit from './NightAudit';
import WebCheckinManagement from './WebCheckinManagement';
import AuditLogs from './AuditLogs';
import ReconciliationDashboard from './ReconciliationDashboard';
import SystemControl from './SystemControl';
import {
  getAccommodationDisplay,
  getPrimaryCourse,
  getCourseCount,
  getGuestCount,
  getAllRooms,
  getAllBeds,
  getAllCourses
} from '../utils/bookingHelpers';
import { ROLES, PERMISSIONS, hasPermission, canAccessAdminDashboard, getRoleInfo } from '../utils/rbac';

// Mobile Components
import MobileNavigation from './mobile/MobileNavigation';
import MobileBookingsView from './mobile/MobileBookingsView';
import MobileEditBookingModalWrapper from './mobile/MobileEditBookingModalWrapper';
import { useIsMobile, useDeviceType } from '../hooks';
import { BarChart3 as BarChart3Icon, Calendar as CalendarIcon, ClipboardList as ClipboardListIcon, Home as HomeIcon, Hourglass as HourglassIcon, Inbox as InboxIcon, Moon as MoonIcon, Pencil as PencilIcon, User as UserIcon, LogOut as LogOutIcon, Lock as LockIcon } from 'lucide-react';
import dridhLogo from '../assets/dridh-logo.png';
import { AgentDrawer, AgentLauncher, AgentProvider, AgentWorkspace, usePmsDataRefresh, workspaceSectionKey, workspaceSessionKey } from '../agent';

function AdminDashboard({ user, onUserUpdated }) {
  // STAFF role sees availability calendar; all other roles see bookings by default
  const [activeTab, setActiveTab] = useState(user?.role === 'STAFF' ? 'availability' : 'bookings');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Mobile detection
  const isMobile = useIsMobile();
  const deviceType = useDeviceType();

  // Night Audit State
  const [nightAuditStatus, setNightAuditStatus] = useState(null);
  const [nightAuditLoading, setNightAuditLoading] = useState(false);
  const [showNightAuditModal, setShowNightAuditModal] = useState(false);

  // Utility function to check if two dates are the same day
  const isSameDay = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  // Utility function to validate check-in date (allows current date and previous date, or always allows for SUPERADMIN/PROPERTYADMIN)
  const canCheckIn = (booking) => {
    // SUPERADMIN and PROPERTYADMIN can check-in anytime
    if (user.role === 'SUPERADMIN' || user.role === ROLES.PROPERTYADMIN) {
      return true;
    }
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    
    const checkInDate = new Date(booking.checkIn);
    return isSameDay(today, checkInDate) || isSameDay(yesterday, checkInDate);
  };

  // Utility function to validate check-out date (allows current date and previous date, or always allows for SUPERADMIN/PROPERTYADMIN)
  const canCheckOut = (booking) => {
    // SUPERADMIN and PROPERTYADMIN can check-out anytime
    if (user.role === 'SUPERADMIN' || user.role === ROLES.PROPERTYADMIN) {
      return true;
    }
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    
    const checkOutDate = new Date(booking.checkOut);
    return isSameDay(today, checkOutDate) || isSameDay(yesterday, checkOutDate);
  };

  // Role-based tab ordering and access control
  const getAvailableTabs = () => {
    const tabs = [
      // Dridh Agent workspace - super admins only for now (the backend enforces the same)
      {
        id: 'agent',
        label: 'Agent',
        roles: [ROLES.SUPERADMIN]
      },

      // Bookings - Not accessible to STAFF (personal booking details and guest info are restricted)
      {
        id: 'bookings',
        label: 'Bookings',
        roles: [ROLES.PROPERTYADMIN, ROLES.SUPERADMIN, ROLES.ACCOUNTS, ROLES.INVENTORY, ROLES.SERVICE],
        permission: PERMISSIONS.BOOKING_READ
      },
      
      // Night Audit - Operations staff only
      { 
        id: 'night-audit', 
        label: 'Night Audit', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN],
        permission: PERMISSIONS.BOOKING_READ
      },
      
      // Web Check-ins - Operations staff only
      { 
        id: 'web-checkins', 
        label: 'Web Check-ins', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN],
        permission: PERMISSIONS.BOOKING_READ
      },
      
      // Staff Reservation - Operations staff only
      { 
        id: 'staff-reservation', 
        label: 'Staff Reservation', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN],
        permission: PERMISSIONS.BOOKING_CREATE
      },
      
      // Services - Operations, Inventory, and Service roles
      { 
        id: 'services', 
        label: 'Services', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN, ROLES.INVENTORY, ROLES.SERVICE],
        permission: PERMISSIONS.ADDON_READ
      },
      
      // Course Management - Operations staff only
      { 
        id: 'courses', 
        label: 'Course Management', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN],
        permission: PERMISSIONS.COURSE_READ
      },
      
      // Add-ons Management - Operations and Inventory roles
      { 
        id: 'addons', 
        label: 'Add-ons Management', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN, ROLES.INVENTORY],
        permission: PERMISSIONS.ADDON_READ
      },
      
      // Items & Inventory - Operations and Inventory roles only
      { 
        id: 'items', 
        label: 'Items & Inventory', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN, ROLES.INVENTORY],
        permission: PERMISSIONS.INVENTORY_READ
      },
      
      // Setup Management - Admin only
      { 
        id: 'setup', 
        label: 'Setup Management', 
        roles: [ROLES.PROPERTYADMIN, ROLES.SUPERADMIN],
        permission: PERMISSIONS.PROPERTY_UPDATE
      },
      
      // Room Management - Operations staff only
      { 
        id: 'rooms', 
        label: 'Room Management', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN],
        permission: PERMISSIONS.ROOM_READ
      },
      
      // Dynamic Pricing - Operations staff only
      { 
        id: 'pricing', 
        label: 'Dynamic Pricing', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN],
        permission: PERMISSIONS.ROOM_UPDATE
      },
      
      // Payment Log - Operations and Accounts
      { 
        id: 'payment-log', 
        label: 'Payment Log', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN, ROLES.ACCOUNTS],
        permission: PERMISSIONS.PAYMENT_READ
      },
      
      
      
      // New Reports - Operations, Admin and Accounts
      { 
        id: 'new-reports', 
        label: 'New Reports', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN, ROLES.ACCOUNTS],
        permission: PERMISSIONS.REPORT_READ
      },
      
      // Custom Reports - Operations, Admin and Accounts
      { 
        id: 'custom-reports', 
        label: 'Custom Reports', 
        roles: [ROLES.STAFF, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN, ROLES.ACCOUNTS],
        permission: PERMISSIONS.REPORT_READ
      },
      
      // User Management - Admin only
      { 
        id: 'users', 
        label: 'User Management', 
        roles: [ROLES.PROPERTYADMIN, ROLES.SUPERADMIN],
        permission: PERMISSIONS.USER_READ
      },
      
      // Audit Logs - Admin only
      { 
        id: 'audit', 
        label: 'Audit Logs', 
        roles: [ROLES.PROPERTYADMIN, ROLES.SUPERADMIN],
        permission: PERMISSIONS.AUDIT_READ
      },
      
      // Availability Calendar - Operations staff only
      {
        id: 'availability',
        label: 'Availability Calendar',
        roles: [ROLES.STAFF, ROLES.ACCOUNTS, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN],
        permission: PERMISSIONS.BOOKING_READ
      },

      // Payment Reconciliation - Staff, Accounts and Admin
      { 
        id: 'reconciliation', 
        label: 'Payment Reconciliation', 
        roles: [ROLES.STAFF, ROLES.ACCOUNTS, ROLES.PROPERTYADMIN, ROLES.SUPERADMIN],
        permission: PERMISSIONS.PAYMENT_READ
      },

      // System Control (kill switch) - Super Admin only
      {
        id: 'system-control',
        label: 'System Control',
        roles: [ROLES.SUPERADMIN]
      }
    ];

    // Filter tabs based on role and permissions
    return tabs.filter(tab => {
      // Check if user's role is in the allowed roles
      const hasRole = tab.roles.includes(user.role);
      // Check if user has the required permission
      const hasPerm = tab.permission ? hasPermission(user.role, tab.permission) : true;
      return hasRole && hasPerm;
    }).sort((a, b) => {
      // Sort by first appearance in the roles list
      const roleOrder = [ROLES.SUPERADMIN, ROLES.PROPERTYADMIN, ROLES.STAFF, ROLES.ACCOUNTS, ROLES.INVENTORY, ROLES.SERVICE, ROLES.GUEST];
      const minRoleA = Math.min(...a.roles.map(r => roleOrder.indexOf(r)).filter(i => i !== -1));
      const minRoleB = Math.min(...b.roles.map(r => roleOrder.indexOf(r)).filter(i => i !== -1));
      return minRoleA - minRoleB;
    });
  };

  // options.silent: reload without the loading state (used after agent changes)
  const loadData = useCallback(async (options) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      switch (activeTab) {
        case 'bookings':
          // Fetch all pages of bookings with pagination support
          try {
            const perPage = 500;
            let pageNum = 1;
            let totalPages = 1;
            const accumulated = [];
            const safetyLimit = 50;

            console.log('Starting bookings pagination fetch...');

            while (pageNum <= totalPages && pageNum <= safetyLimit) {
              const resp = await bookingsService.getAll({
                page: pageNum,
                limit: perPage,
                sortBy: 'createdAt',
                sortOrder: 'desc'
              });

              console.log(`Fetched page ${pageNum}:`, resp.pagination);

              // Normalize response
              let pageItems = [];
              if (resp && resp.success && resp.data) {
                if (Array.isArray(resp.data)) {
                  pageItems = resp.data;
                } else if (Array.isArray(resp.data.bookings)) {
                  pageItems = resp.data.bookings;
                }
              } else if (Array.isArray(resp)) {
                pageItems = resp;
              }

              accumulated.push(...pageItems);

              // Update totalPages from pagination metadata
              if (resp.pagination && resp.pagination.totalPages) {
                totalPages = Number(resp.pagination.totalPages);
              } else if (pageItems.length < perPage) {
                totalPages = pageNum; // reached last page
              }

              pageNum += 1;
            }

            setBookings(accumulated);
            console.log(`Loaded ${accumulated.length} bookings total across ${pageNum - 1} pages`);
          } catch (error) {
            console.error('Error during bookings pagination:', error);
            if (!silent) setBookings([]);
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Failed to load bookings:', error);
      if (!silent) {
        toast.error('Failed to load data');
        setBookings([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user.role, activeTab]);

  useEffect(() => {
    loadData();
    // Fetch night audit status when bookings tab is active
    if (activeTab === 'bookings') {
      fetchNightAuditStatus();
    }
  }, [loadData, activeTab]);

  // The PMS agent checked guests in or out, recorded a payment or edited notes: reload the list in place.
  usePmsDataRefresh(['bookings', 'payments'], () => {
    if (activeTab !== 'bookings') return;
    loadData({ silent: true });
    fetchNightAuditStatus();
  });

  const handleBookingConfirm = async (bookingId) => {
    try {
      await bookingsService.confirm(bookingId);
      toast.success('Booking confirmed successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to confirm booking');
    }
  };

  const handleBookingCancel = async (bookingId) => {
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      try {
        await bookingsService.cancel(bookingId);
        toast.success('Booking cancelled successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to cancel booking');
      }
    }
  };

  const handleBookingCheckIn = async (bookingId) => {
    // Find the booking to validate dates
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) {
      toast.error('Booking not found');
      return;
    }

    // Check if today or yesterday is the check-in date
    if (!canCheckIn(booking)) {
      const checkInDate = new Date(booking.checkIn).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      toast.error(`Check-in is only allowed on the scheduled check-in date or the day after: ${checkInDate}`);
      return;
    }

    if (window.confirm('Are you sure you want to check in this guest?')) {
      try {
        await bookingsService.checkIn(bookingId);
        toast.success('Guest checked in successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to check in guest');
      }
    }
  };

  const handleBookingCheckOut = async (bookingId) => {
    // Find the booking to validate dates
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) {
      toast.error('Booking not found');
      return;
    }

    // Check if today or yesterday is the check-out date
    if (!canCheckOut(booking)) {
      const checkOutDate = new Date(booking.checkOut).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      toast.error(`Check-out is only allowed on the scheduled check-out date or the day after: ${checkOutDate}`);
      return;
    }

    if (window.confirm('Are you sure you want to check out this guest?')) {
      try {
        const response = await bookingsService.checkOut(bookingId);
        
        // Handle payment warnings from backend
        if (response && response.warning) {
          toast.warning(response.warning, { autoClose: 8000 });
          toast.success('Guest checked out successfully');
        } else {
          toast.success('Guest checked out successfully');
        }
        
        loadData();
      } catch (error) {
        // Handle payment validation errors from backend
        const errorMessage = error.response?.data?.error || error.error || error.message || 'Failed to check out guest';
        toast.error(errorMessage, { autoClose: 10000 });
      }
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      try {
        // Clear any local storage data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Reload the page to redirect to login
        window.location.reload();
        
        toast.success('Logged out successfully');
      } catch (error) {
        console.error('Logout error:', error);
        // Force logout even if there's an error
        window.location.reload();
      }
    }
  };

  const handleProfileClick = () => {
    setShowProfileModal(true);
  };

  // Night Audit Functions
  const fetchNightAuditStatus = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const response = await nightAuditService.getByDate(today);
      setNightAuditStatus(response.data);
    } catch (error) {
      // If no audit exists for today, status will remain null
      if (error.status !== 404) {
        console.error('Error fetching night audit status:', error);
      }
    }
  };

  const startNightAudit = async () => {
    setNightAuditLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const auditData = {
        auditDate: today,
        notes: 'Night audit started from booking management',
        checkinProcessed: false,
        checkoutProcessed: false,
        paymentsVerified: false,
        occupancyVerified: false,
        reportsGenerated: false
      };

      const response = await nightAuditService.create(auditData);
      setNightAuditStatus(response.data);
      toast.success('Night audit started successfully');
      loadData(); // Refresh bookings data
    } catch (error) {
      if (error.error && error.error.includes('already exists')) {
        toast.error('Night audit for today already exists');
        // Fetch the existing audit
        fetchNightAuditStatus();
      } else {
        toast.error('Failed to start night audit');
      }
    } finally {
      setNightAuditLoading(false);
    }
  };

  // Simplified Night Audit: Only start and complete functionality
  const completeNightAudit = async () => {
    if (!nightAuditStatus) return;

    if (window.confirm('Are you sure you want to complete the night audit? This will mark all tasks as completed and restrict past date operations.')) {
      setNightAuditLoading(true);
      try {
        const response = await nightAuditService.complete(nightAuditStatus.id);
        setNightAuditStatus(response.data);
        toast.success('Night audit completed successfully');
        loadData(); // Refresh bookings data
      } catch (error) {
        toast.error('Failed to complete night audit');
      } finally {
        setNightAuditLoading(false);
      }
    }
  };

  const reopenNightAudit = async () => {
    if (!nightAuditStatus || user.role !== ROLES.SUPERADMIN) return;

    if (window.confirm('Are you sure you want to reopen the night audit? This will allow modifications to past date operations.')) {
      setNightAuditLoading(true);
      try {
        const response = await nightAuditService.reopen(nightAuditStatus.id);
        setNightAuditStatus(response.data);
        toast.success('Night audit reopened successfully');
        loadData(); // Refresh bookings data
      } catch (error) {
        toast.error('Failed to reopen night audit');
      } finally {
        setNightAuditLoading(false);
      }
    }
  };

  // Check if user has access to admin dashboard
  if (!user || !canAccessAdminDashboard(user.role)) {
    const roleInfo = getRoleInfo(user?.role);
    return (
      <div className="card" style={{
        textAlign: 'center',
        padding: '60px 20px',
        maxWidth: '600px',
        margin: '60px auto'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}><LockIcon size="1em" aria-hidden="true" /></div>
        <h2 style={{ marginBottom: '12px', color: '#1A3E2A' }}>Access Denied</h2>
        <p style={{ color: '#5F6B61', fontSize: '16px', marginBottom: '8px' }}>
          Your role: <strong>{roleInfo.label}</strong>
        </p>
        <p style={{ color: '#5F6B61', fontSize: '14px' }}>
          You don't have permission to access the admin dashboard.
        </p>
        <p style={{ color: '#5F6B61', fontSize: '14px', marginTop: '16px' }}>
          Please contact your administrator if you believe this is an error.
        </p>
      </div>
    );
  }

  const availableTabs = getAvailableTabs();

  return (
    <AgentProvider user={user} activeTab={activeTab} tabLabel={availableTabs.find((tab) => tab.id === activeTab)?.label}>
    <>
      {/* CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="admin-dashboard" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      backgroundColor: '#F5F3EB',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      zIndex: 1
    }}>
      {/* Mobile Navigation - Only show on mobile */}
      {isMobile && (
        <MobileNavigation
          user={user}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          availableTabs={getAvailableTabs()}
          onLogout={handleLogout}
        />
      )}
      
      {/* Desktop: brand header */}
      {!isMobile && (
        <header className="brand-header">
          <div className="brand-header__identity">
            <img src={dridhLogo} alt="Dridh" className="brand-header__logo" />
            <span className="brand-header__divider" aria-hidden="true" />
            <div style={{ minWidth: 0 }}>
              <h1 className="brand-header__title">
                Welcome back, {user.name}!
              </h1>
              <p className="brand-header__subtitle">
                Manage Users & Bookings
              </p>
            </div>
          </div>

          <div className="brand-header__actions">
            <button
              onClick={handleProfileClick}
              className="brand-header-btn"
              title="View your profile"
            >
              <UserIcon size={16} aria-hidden="true" />
              Profile
            </button>
            <button
              onClick={handleLogout}
              className="brand-header-btn brand-header-btn--danger"
              title="Logout from admin panel"
            >
              <LogOutIcon size={16} aria-hidden="true" />
              Logout
            </button>
          </div>
        </header>
      )}

      {/* Navigation Tabs - Desktop only */}
      {!isMobile && (
      <div style={{
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #E3DFD3',
        padding: '0 24px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center'
      }}>
        <div className="nav-links" style={{
          display: 'flex',
          gap: '4px',
          margin: 0,
          padding: '10px 0',
          flexWrap: 'wrap',
          overflowX: 'auto'
        }}>
          {availableTabs.map(tab => (
            <button
              key={tab.id}
              className={`brand-tab ${activeTab === tab.id ? 'brand-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      )} {/* end !isMobile desktop navigation */}

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isMobile ? '#F5F3EB' : 'white',
        margin: 0
      }}>
        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            fontSize: '18px',
            color: '#5F6B61'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '16px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
              Loading...
            </div>
          </div>
        ) : (
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: isMobile ? '0' : '24px',
            backgroundColor: isMobile ? '#F5F3EB' : '#ffffff'
          }}>
            {activeTab === 'bookings' && (
              <BookingsTab
                bookings={bookings}
                onConfirm={handleBookingConfirm}
                onCancel={handleBookingCancel}
                onCheckIn={handleBookingCheckIn}
                onCheckOut={handleBookingCheckOut}
                canCheckIn={canCheckIn}
                canCheckOut={canCheckOut}
                nightAuditStatus={nightAuditStatus}
                nightAuditLoading={nightAuditLoading}
                onStartNightAudit={startNightAudit}
                onCompleteNightAudit={completeNightAudit}
                onReopenNightAudit={reopenNightAudit}
                setShowNightAuditModal={setShowNightAuditModal}
                user={user}
              />
            )}
            {activeTab === 'night-audit' && (
              <NightAudit
                user={user}
              />
            )}
            {activeTab === 'web-checkins' && (
              <WebCheckinManagement
                user={user}
              />
            )}
            {activeTab === 'staff-reservation' && (
              <StaffReservation
                user={user}
                onReservationCreated={loadData}
              />
            )}
            {activeTab === 'setup' && (
              <PropertyRoomTypeManager
                user={user}
                onDataUpdate={loadData}
              />
            )}
            {activeTab === 'rooms' && (
              <RoomManagement
                onRoomUpdate={loadData}
              />
            )}
            {activeTab === 'pricing' && (
              <PriceManagement
                user={user}
                onDataUpdate={loadData}
              />
            )}
            {activeTab === 'payment-log' && (
              <PaymentLog />
            )}
            {activeTab === 'reports' && (
              <Reports user={user} />
            )}
            {activeTab === 'new-reports' && (
              <NewReports user={user} />
            )}
            {activeTab === 'custom-reports' && (
              <CustomReports user={user} />
            )}
            {activeTab === 'debug' && (
              <ImageDebugger />
            )}
            {activeTab === 'users' && (
              <UserManagementNew 
                currentUser={user}
              />
            )}
            {activeTab === 'audit' && (
              <AuditLogs currentUser={user} />
            )}
            {activeTab === 'courses' && (
              <CourseManagement 
                user={user}
                onDataUpdate={loadData}
              />
            )}
            {activeTab === 'calendar' && (
              <BookingCalendar 
                bookings={bookings}
                onDataUpdate={loadData}
              />
            )}
            {activeTab === 'availability' && (
              <AvailabilityCalendar 
                bookings={bookings}
                onDataUpdate={loadData}
              />
            )}
            {activeTab === 'addons' && (
              <AddOnManagement 
                user={user}
                onDataUpdate={loadData}
              />
            )}
            {activeTab === 'items' && (
              <ItemsManagement 
                user={user}
              />
            )}
            {activeTab === 'services' && (
              <ServicesBooking 
                user={user}
              />
            )}
            {activeTab === 'reconciliation' && (
              <ReconciliationDashboard 
                user={user}
              />
            )}
            {activeTab === 'agent' && <AgentWorkspace user={user} />}
            {activeTab === 'system-control' && (
              <SystemControl 
                user={user}
              />
            )}
          </div>
        )}
      </div>

      {/* Night Audit Modal */}
      {showNightAuditModal && (
        <NightAuditModal
          nightAuditStatus={nightAuditStatus}
          nightAuditLoading={nightAuditLoading}
          onClose={() => setShowNightAuditModal(false)}
          onStartNightAudit={startNightAudit}
          onCompleteNightAudit={completeNightAudit}
          onReopenNightAudit={reopenNightAudit}
          user={user}
        />
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal 
          user={user} 
          onClose={() => setShowProfileModal(false)}
          onUserUpdated={onUserUpdated} 
        />
      )}
    </div>

      {/* Dridh Agent: launcher + drawer on every tab except the Agent tab itself */}
      {activeTab !== 'agent' && (
        <>
          <AgentLauncher />
          <AgentDrawer
            onOpenWorkspace={(sessionId, section) => {
              if (sessionId) localStorage.setItem(workspaceSessionKey(user.id), sessionId);
              localStorage.setItem(workspaceSectionKey(user.id), section || 'conversations');
              setActiveTab('agent');
            }}
          />
        </>
      )}
    </>
    </AgentProvider>
  );
}

// Calendar View Component for Bookings
function CalendarView({ bookings, currentDate, onDateChange, onBookingClick, getStatusBadge }) {
  // Helper to get bookings for a specific date
  const getBookingsForDate = (date) => {
    return bookings.filter(booking => {
      if (!booking.checkIn || !booking.checkOut) return false;
      const checkIn = new Date(booking.checkIn);
      const checkOut = new Date(booking.checkOut);
      const targetDate = new Date(date);
      
      // Remove time portion for accurate comparison
      checkIn.setHours(0, 0, 0, 0);
      checkOut.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);
      
      return targetDate >= checkIn && targetDate <= checkOut;
    });
  };

  // Helper to get booking color
  const getBookingColor = (booking) => {
    switch (booking.status) {
      case 'CONFIRMED': return '#1A3E2A';
      case 'PENDING': return '#B68235';
      case 'CANCELLED': return '#A13F2F';
      case 'CHECKEDIN': return '#1A3E2A';
      case 'CHECKEDOUT': return '#5F6B61';
      case 'COMPLETED': return '#A9613C';
      default: return '#1A3E2A';
    }
  };

  // Helper to get booking icon
  const getBookingIcon = (booking) => {
    switch (booking.status) {
      case 'CONFIRMED': return '✓';
      case 'PENDING': return '';
      case 'CANCELLED': return '';
      case 'CHECKEDIN': return '';
      case 'CHECKEDOUT': return '';
      case 'COMPLETED': return '';
      default: return '';
    }
  };

  // Calendar navigation
  const nextMonth = () => onDateChange(addMonths(currentDate, 1));
  const prevMonth = () => onDateChange(subMonths(currentDate, 1));
  const today = () => onDateChange(new Date());

  // Render calendar header with navigation
  const renderCalendarHeader = () => {
    const dateFormat = 'MMMM yyyy';
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '20px',
        backgroundColor: '#F5F3EB',
        borderRadius: '12px 12px 0 0',
        borderBottom: '2px solid #E3DFD3'
      }}>
        <button
          onClick={prevMonth}
          style={{
            backgroundColor: '#1A3E2A',
            color: 'white',
            border: 'none',
            padding: '10px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#163626'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#1A3E2A'}
        >
          ← Previous
        </button>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#24332A' }}>
            {format(currentDate, dateFormat)}
          </h3>
          <button
            onClick={today}
            style={{
              backgroundColor: '#EEEBE1',
              color: '#36433B',
              border: '1px solid #D2CDBF',
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#E3DFD3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#EEEBE1'}
          >
            Today
          </button>
        </div>
        
        <button
          onClick={nextMonth}
          style={{
            backgroundColor: '#1A3E2A',
            color: 'white',
            border: 'none',
            padding: '10px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#163626'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#1A3E2A'}
        >
          Next →
        </button>
      </div>
    );
  };

  // Render days of week header
  const renderDaysOfWeek = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return (
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        backgroundColor: '#F5F3EB',
        borderBottom: '1px solid #E3DFD3'
      }}>
        {days.map(day => (
          <div
            key={day}
            style={{
              padding: '12px',
              textAlign: 'center',
              fontWeight: '700',
              fontSize: '14px',
              color: '#5F6B61',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            {day.slice(0, 3)}
          </div>
        ))}
      </div>
    );
  };

  // Render calendar days
  const renderCalendarDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const currentDay = day;
        const dayBookings = getBookingsForDate(currentDay);
        const isCurrentMonth = isSameMonth(currentDay, monthStart);
        const isToday = isSameDay(currentDay, new Date());

        days.push(
          <div
            key={currentDay.toString()}
            style={{
              backgroundColor: isCurrentMonth ? 'white' : '#F5F3EB',
              border: '1px solid #E3DFD3',
              borderTop: isToday ? '3px solid #1A3E2A' : '1px solid #E3DFD3',
              padding: '8px',
              minHeight: '120px',
              position: 'relative',
              opacity: isCurrentMonth ? 1 : 0.5,
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ 
              fontWeight: isToday ? '700' : '600',
              marginBottom: '6px',
              fontSize: '14px',
              color: isToday ? '#1A3E2A' : isCurrentMonth ? '#24332A' : '#A5A89B',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{format(currentDay, 'd')}</span>
              {dayBookings.length > 0 && (
                <span style={{
                  backgroundColor: '#1A3E2A',
                  color: 'white',
                  fontSize: '10px',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontWeight: '700'
                }}>
                  {dayBookings.length}
                </span>
              )}
            </div>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '4px',
              maxHeight: '90px',
              overflowY: 'auto'
            }}>
              {dayBookings.slice(0, 3).map((booking, index) => (
                <div
                  key={`${booking.id}-${index}`}
                  onClick={() => onBookingClick(booking)}
                  style={{
                    fontSize: '11px',
                    cursor: 'pointer',
                    backgroundColor: getBookingColor(booking),
                    color: 'white',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  title={`${booking.user?.name || booking.user?.email || 'Guest'} - ${getAccommodationDisplay(booking)}`}
                >
                  <span>{getBookingIcon(booking)}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {booking.user?.name || booking.user?.email?.split('@')[0] || 'Guest'}
                  </span>
                </div>
              ))}
              {dayBookings.length > 3 && (
                <div style={{
                  fontSize: '10px',
                  color: '#5F6B61',
                  fontWeight: '600',
                  textAlign: 'center',
                  padding: '2px'
                }}>
                  +{dayBookings.length - 3} more
                </div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {days}
        </div>
      );
      days = [];
    }

    return <div>{rows}</div>;
  };

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
      {renderCalendarHeader()}
      {renderDaysOfWeek()}
      {renderCalendarDays()}
      
      {/* Legend */}
      <div style={{ 
        padding: '16px 20px', 
        backgroundColor: '#F5F3EB', 
        borderTop: '1px solid #E3DFD3',
        display: 'flex',
        gap: '20px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ fontWeight: '600', color: '#5F6B61', fontSize: '13px' }}>Status Legend:</div>
        {[
          { status: 'CONFIRMED', label: 'Confirmed', color: '#1A3E2A' },
          { status: 'PENDING', label: 'Pending', color: '#B68235' },
          { status: 'CHECKEDIN', label: 'Checked In', color: '#1A3E2A' },
          { status: 'CHECKEDOUT', label: 'Checked Out', color: '#5F6B61' },
          { status: 'CANCELLED', label: 'Cancelled', color: '#A13F2F' }
        ].map(({ status, label, color }) => (
          <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: color, 
              borderRadius: '3px' 
            }}></div>
            <span style={{ fontSize: '12px', color: '#5F6B61' }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Bookings Tab Component
function BookingsTab({ 
  bookings, 
  onConfirm, 
  onCancel, 
  onCheckIn, 
  onCheckOut, 
  canCheckIn, 
  canCheckOut,
  nightAuditStatus,
  nightAuditLoading,
  onStartNightAudit,
  onCompleteNightAudit,
  onReopenNightAudit,
  setShowNightAuditModal,
  user
}) {
  const [editingPaymentStatus, setEditingPaymentStatus] = useState(null);
  const [newPaymentStatus, setNewPaymentStatus] = useState('');
  const [editingBookingStatus, setEditingBookingStatus] = useState(null);
  const [newBookingStatus, setNewBookingStatus] = useState('');
  const [editingBooking, setEditingBooking] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [viewMode, setViewMode] = useState('blocks'); // 'list', 'blocks', or 'calendar'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState(''); // Single date filter (kept for backward compatibility)
  const [startDateFilter, setStartDateFilter] = useState(''); // Date range start
  const [endDateFilter, setEndDateFilter] = useState(''); // Date range end
  const [downloadingInvoice, setDownloadingInvoice] = useState(null); // Track which invoice is being downloaded
  const [showInvoiceModal, setShowInvoiceModal] = useState(false); // Invoice selection modal state
  const [selectedBookingForInvoice, setSelectedBookingForInvoice] = useState(null); // Selected booking for invoice
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date()); // For calendar navigation

  // Mobile detection
  const isMobile = useIsMobile();

  // Helper functions for Night Audit button
  const getNightAuditButtonColor = () => {
    if (!nightAuditStatus) return '#1A3E2A'; // Blue for "Start Audit"
    if (nightAuditStatus.status === 'COMPLETED') return '#1A3E2A'; // Green for completed
    return '#B68235'; // Orange for in-progress
  };

  const getNightAuditButtonText = () => {
    if (!nightAuditStatus) return 'Start Night Audit';
    if (nightAuditStatus.status === 'COMPLETED') return 'Audit Completed';
    return 'Night Audit Active';
  };

  const getNightAuditIcon = () => {
    if (!nightAuditStatus) return '';
    if (nightAuditStatus.status === 'COMPLETED') return '';
    return '';
  };

  const getNightAuditButtonTooltip = () => {
    if (!nightAuditStatus) return 'Click to start night audit for today';
    if (nightAuditStatus.status === 'COMPLETED') return 'Night audit is completed. Click to view details.';
    return 'Night audit is in progress. Click to view/update progress.';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'CONFIRMED': { color: '#1A3E2A', bg: '#E1E9E2', text: '✓ Confirmed' },
      'PENDING': { color: '#B68235', bg: '#F3E6CB', text: 'Pending' },
      'CANCELLED': { color: '#A13F2F', bg: '#E5B3A9', text: 'Cancelled' },
      'CHECKEDIN': { color: '#1A3E2A', bg: '#E1E9E2', text: 'Checked In' },
      'CHECKEDOUT': { color: '#5F6B61', bg: '#EEEBE1', text: 'Checked Out' },
      'COMPLETED': { color: '#A9613C', bg: '#E4C2AC', text: 'Completed' }
    };
    const config = statusConfig[status] || statusConfig['PENDING'];
    return (
      <span style={{
        backgroundColor: config.bg,
        color: config.color,
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        border: `1px solid ${config.color}20`
      }}>
        {config.text}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'SUCCESS': { color: '#1A3E2A', bg: '#E1E9E2', text: 'Paid' },
      'PENDING': { color: '#B68235', bg: '#F3E6CB', text: 'Pending' },
      'FAILED': { color: '#A13F2F', bg: '#E5B3A9', text: 'Failed' },
      'REFUNDED': { color: '#1A3E2A', bg: '#E1E9E2', text: 'Refunded' }
    };
    const config = statusConfig[status] || statusConfig['PENDING'];
    return (
      <span style={{
        backgroundColor: config.bg,
        color: config.color,
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        border: `1px solid ${config.color}20`
      }}>
        {config.text}
      </span>
    );
  };

  // Filter and search bookings
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = !searchTerm || 
      booking.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.room?.roomNumber?.toString().includes(searchTerm);
    
    const matchesStatus = statusFilter === 'ALL' || booking.status === statusFilter;
    
    // Date range filter logic - check if booking overlaps with date range
    let matchesDateRange = true;
    if (startDateFilter || endDateFilter) {
      const bookingCheckIn = booking.checkIn ? new Date(booking.checkIn) : null;
      const bookingCheckOut = booking.checkOut ? new Date(booking.checkOut) : null;
      
      if (bookingCheckIn && bookingCheckOut) {
        const rangeStart = startDateFilter ? new Date(startDateFilter) : null;
        const rangeEnd = endDateFilter ? new Date(endDateFilter) : null;
        
        // Check if booking overlaps with the selected date range
        if (rangeStart && rangeEnd) {
          // Booking overlaps if: booking start <= range end AND booking end >= range start
          matchesDateRange = bookingCheckIn <= rangeEnd && bookingCheckOut >= rangeStart;
        } else if (rangeStart) {
          // Only start date: booking must start on or after this date
          matchesDateRange = bookingCheckOut >= rangeStart;
        } else if (rangeEnd) {
          // Only end date: booking must end on or before this date
          matchesDateRange = bookingCheckIn <= rangeEnd;
        }
      }
    }
    
    // Legacy single date filter (kept for backward compatibility)
    const matchesDate = !dateFilter || (booking.checkIn && 
      format(new Date(booking.checkIn), 'yyyy-MM-dd') === dateFilter);
    
    return matchesSearch && matchesStatus && matchesDateRange && matchesDate;
  });

  // Group bookings by check-in date
  const groupedBookings = filteredBookings.reduce((groups, booking) => {
    const checkInDate = booking.checkIn 
      ? format(new Date(booking.checkIn), 'yyyy-MM-dd')
      : 'no-date';
    
    if (!groups[checkInDate]) {
      groups[checkInDate] = [];
    }
    groups[checkInDate].push(booking);
    return groups;
  }, {});

  // Sort groups by date
  const sortedGroups = Object.entries(groupedBookings).sort(([dateA], [dateB]) => {
    if (dateA === 'no-date') return 1;
    if (dateB === 'no-date') return -1;
    return new Date(dateA) - new Date(dateB);
  });

  const handlePaymentStatusChange = async (bookingId, status) => {
    try {
      const response = await bookingsService.update(bookingId, { paymentStatus: status });
      if (response.success) {
        toast.success('Payment status updated successfully');
        setEditingPaymentStatus(null);
        // Refresh bookings data
        window.location.reload();
      }
    } catch (error) {
      toast.error('Failed to update payment status');
      console.error('Payment status update error:', error);
    }
  };

  const startEditingPaymentStatus = (bookingId, currentStatus) => {
    setEditingPaymentStatus(bookingId);
    setNewPaymentStatus(currentStatus);
  };

  const cancelEditingPaymentStatus = () => {
    setEditingPaymentStatus(null);
    setNewPaymentStatus('');
  };

  const startEditingBookingStatus = (bookingId, currentStatus) => {
    setEditingBookingStatus(bookingId);
    setNewBookingStatus(currentStatus);
  };

  const cancelEditingBookingStatus = () => {
    setEditingBookingStatus(null);
    setNewBookingStatus('');
  };

  const saveBookingStatus = async (bookingId) => {
    // Find the booking to validate dates
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) {
      toast.error('Booking not found');
      return;
    }

    // Date validation for check-in and check-out status changes
    // Property admins and super admins can bypass date restrictions
    const isPrivilegedUser = user && (user.role === ROLES.PROPERTYADMIN || user.role === ROLES.SUPERADMIN);

    if (newBookingStatus === 'CHECKEDIN') {
      if (!isPrivilegedUser && !canCheckIn(booking)) {
        const checkInDate = new Date(booking.checkIn).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        toast.error(`Check-in status can only be set on the scheduled check-in date or the day after: ${checkInDate}`);
        return;
      }
    }
    
    if (newBookingStatus === 'CHECKEDOUT') {
      if (!isPrivilegedUser && !canCheckOut(booking)) {
        const checkOutDate = new Date(booking.checkOut).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        toast.error(`Check-out status can only be set on the scheduled check-out date or the day after: ${checkOutDate}`);
        return;
      }
    }

    try {
      const response = await bookingsService.update(bookingId, { status: newBookingStatus });
      
      // Handle payment warnings from backend (for CHECKEDOUT status)
      if (response && response.warning) {
        toast.warning(response.warning, { autoClose: 8000 });
        toast.success('Booking status updated successfully');
      } else {
        toast.success('Booking status updated successfully');
      }
      
      setEditingBookingStatus(null);
      setNewBookingStatus('');
      // Refresh bookings by calling the parent component's load function
      window.location.reload(); // Simple refresh for now
    } catch (error) {
      // Handle payment validation errors from backend (for CHECKEDOUT status)
      const errorMessage = error.response?.data?.error || error.error || error.message || 'Failed to update booking status';
      toast.error(errorMessage, { autoClose: 10000 });
      console.error('Booking status update error:', error);
    }
  };

  const openEditBookingModal = (booking) => {
    setEditingBooking(booking);
    setShowEditModal(true);
  };

  const closeEditBookingModal = () => {
    setEditingBooking(null);
    setShowEditModal(false);
  };

  const handleBookingUpdate = () => {
    closeEditBookingModal();
    // Refresh the page to reload bookings
    window.location.reload();
  };

  const handleDownloadInvoice = async (bookingId) => {
    // Find the booking details
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) {
      toast.error('Booking not found. Please try again.');
      return;
    }
    
    // Set selected booking and show modal
    setSelectedBookingForInvoice(booking);
    setShowInvoiceModal(true);
  };

  // Handle standard invoice download
  const handleStandardInvoiceDownload = async (bookingId, companyDetails = {}) => {
    setDownloadingInvoice(bookingId);
    try {
      await invoiceService.downloadInvoice(bookingId, companyDetails);
      toast.success('Standard invoice downloaded successfully!');
      setShowInvoiceModal(false);
    } catch (error) {
      console.error('Failed to download standard invoice:', error);
      toast.error('Failed to download standard invoice. Please try again.');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  // Handle flexible invoice download
  const handleFlexibleInvoiceDownload = async (bookingId, options) => {
    setDownloadingInvoice(bookingId);
    try {
      await invoiceService.downloadFlexibleInvoice(bookingId, options);
      toast.success('Flexible invoice downloaded successfully!');
      setShowInvoiceModal(false);
    } catch (error) {
      console.error('Failed to download flexible invoice:', error);
      toast.error('Failed to download flexible invoice. Please try again.');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  // Close invoice modal
  const handleCloseInvoiceModal = () => {
    if (!downloadingInvoice) {
      setShowInvoiceModal(false);
      setSelectedBookingForInvoice(null);
    }
  };

  // Mobile view - return mobile-optimized component
  if (isMobile) {
    return (
      <>
        <MobileBookingsView
          bookings={bookings}
          onEditBooking={openEditBookingModal}
          getStatusBadge={getStatusBadge}
          getPaymentStatusBadge={getPaymentStatusBadge}
          loading={false}
        />
        
        {/* Mobile Edit Booking Modal */}
        {showEditModal && editingBooking && (
          <MobileEditBookingModalWrapper
            booking={editingBooking}
            user={user}
            onClose={closeEditBookingModal}
            onUpdate={handleBookingUpdate}
            canCheckIn={canCheckIn}
            canCheckOut={canCheckOut}
            isMobile={true}
            DesktopModalComponent={EditBookingModal}
          />
        )}
        
        {/* Invoice Selection Modal */}
        {showInvoiceModal && selectedBookingForInvoice && (
          <InvoiceSelectionModal
            isOpen={showInvoiceModal}
            onClose={handleCloseInvoiceModal}
            booking={selectedBookingForInvoice}
            onDownloadStandard={handleStandardInvoiceDownload}
            onDownloadFlexible={handleFlexibleInvoiceDownload}
            downloading={!!downloadingInvoice}
          />
        )}
      </>
    );
  }

  return (
    <div>
      {/* Header Section */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: '0', fontSize: '28px', fontWeight: '800', color: '#1A3E2A' }}>
              Bookings Management 
            </h2>
            <p style={{ margin: '5px 0 0 0', color: '#5F6B61', fontSize: '16px' }}>
              Manage and track all guest bookings ({filteredBookings.length} {filteredBookings.length === 1 ? 'booking' : 'bookings'})
            </p>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Night Audit Button */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNightAuditModal(true)}
                disabled={nightAuditLoading}
                style={{
                  backgroundColor: getNightAuditButtonColor(),
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: nightAuditLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  minWidth: '160px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!nightAuditLoading) {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
                title={getNightAuditButtonTooltip()}
              >
                {nightAuditLoading ? (
                  <>
                    <span style={{ 
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid #ffffff40',
                      borderTop: '2px solid #ffffff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '16px' }}>{getNightAuditIcon()}</span>
                    {getNightAuditButtonText()}
                  </>
                )}
              </button>
            </div>

            {/* View Toggle */}
            <div style={{
              display: 'flex',
              backgroundColor: '#EEEBE1',
              padding: '4px',
              borderRadius: '8px',
              gap: '2px'
            }}>
              <button
                onClick={() => setViewMode('blocks')}
                style={{
                  backgroundColor: viewMode === 'blocks' ? '#1A3E2A' : 'transparent',
                  color: viewMode === 'blocks' ? 'white' : '#5F6B61',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                Cards
              </button>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  backgroundColor: viewMode === 'list' ? '#1A3E2A' : 'transparent',
                  color: viewMode === 'list' ? 'white' : '#5F6B61',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                List
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                style={{
                  backgroundColor: viewMode === 'calendar' ? '#1A3E2A' : 'transparent',
                  color: viewMode === 'calendar' ? 'white' : '#5F6B61',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                Calendar
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div style={{ 
          display: 'flex', 
          gap: '16px', 
          alignItems: 'center',
          flexWrap: 'wrap',
          padding: '20px',
          backgroundColor: '#F5F3EB',
          borderRadius: '12px',
          border: '1px solid #E3DFD3'
        }}>
          <div style={{ flex: '1', minWidth: '250px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
              Search Bookings
            </label>
            <input
              type="text"
              placeholder="Search by guest name, email, or room number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            />
          </div>
          
          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
              Filter by Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="ALL">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="CHECKEDIN">Checked In</option>
              <option value="CHECKEDOUT">Checked Out</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div style={{ minWidth: '180px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            />
          </div>

          <div style={{ minWidth: '180px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
              End Date
            </label>
            <input
              type="date"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            />
          </div>

          {(startDateFilter || endDateFilter) && (
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                onClick={() => {
                  setStartDateFilter('');
                  setEndDateFilter('');
                }}
                title="Clear date range filter"
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#A13F2F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                ✕ Clear Dates
              </button>
            </div>
          )}

          {/* Stats Summary */}
          <div style={{ 
            display: 'flex', 
            gap: '16px',
            marginLeft: 'auto',
            alignItems: 'center'
          }}>
            {/* Night Audit Status Indicator */}
            {nightAuditStatus && (
              <div style={{ 
                textAlign: 'center',
                padding: '8px 12px',
                backgroundColor: nightAuditStatus.status === 'COMPLETED' ? '#E1E9E2' : '#F3E6CB',
                borderRadius: '8px',
                border: `1px solid ${nightAuditStatus.status === 'COMPLETED' ? '#1A3E2A' : '#C9903C'}`
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: '700', 
                  color: nightAuditStatus.status === 'COMPLETED' ? '#0F2619' : '#684A1F' 
                }}>
                  {nightAuditStatus.status === 'COMPLETED' ? '' : ''}
                </div>
                <div style={{ 
                  fontSize: '11px', 
                  color: nightAuditStatus.status === 'COMPLETED' ? '#0F2619' : '#684A1F',
                  fontWeight: '600'
                }}>
                  Night Audit
                </div>
              </div>
            )}
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#1A3E2A' }}>
                {bookings.filter(b => b.status === 'CONFIRMED').length}
              </div>
              <div style={{ fontSize: '12px', color: '#5F6B61' }}>Confirmed</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#1A3E2A' }}>
                {bookings.filter(b => b.status === 'CHECKEDIN').length}
              </div>
              <div style={{ fontSize: '12px', color: '#5F6B61' }}>Checked In</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#B68235' }}>
                {bookings.filter(b => b.status === 'PENDING').length}
              </div>
              <div style={{ fontSize: '12px', color: '#5F6B61' }}>Pending</div>
            </div>
          </div>
        </div>
      </div>

      {filteredBookings.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          backgroundColor: '#F5F3EB',
          borderRadius: '12px',
          border: '2px dashed #D2CDBF'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}><CalendarIcon size="1em" aria-hidden="true" /></div>
          <h3 style={{ margin: '0 0 10px 0', color: '#36433B', fontSize: '20px' }}>
            {bookings.length === 0 ? 'No bookings found' : 'No bookings match your filters'}
          </h3>
          <p style={{ color: '#5F6B61', margin: '0', fontSize: '16px' }}>
            {bookings.length === 0 
              ? 'Bookings will appear here once guests make reservations.' 
              : 'Try adjusting your search or filter criteria.'}
          </p>
          
          {/* Night Audit Status in Empty State */}
          {nightAuditStatus && bookings.length === 0 && (
            <div style={{ 
              marginTop: '16px', 
              padding: '12px 16px',
              backgroundColor: nightAuditStatus.status === 'COMPLETED' ? '#E1E9E2' : '#F3E6CB',
              borderRadius: '8px',
              border: `1px solid ${nightAuditStatus.status === 'COMPLETED' ? '#1A3E2A' : '#C9903C'}`
            }}>
              <p style={{ 
                margin: '0', 
                fontSize: '14px', 
                color: nightAuditStatus.status === 'COMPLETED' ? '#0F2619' : '#684A1F',
                fontWeight: '600'
              }}>
                {nightAuditStatus.status === 'COMPLETED' 
                  ? 'Night audit is completed for today' 
                  : 'Night audit is in progress for today'}
              </p>
            </div>
          )}
          
          {searchTerm || statusFilter !== 'ALL' ? (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('ALL');
              }}
              style={{
                marginTop: '16px',
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          ) : null}
        </div>
      ) : (
        <>
          {viewMode === 'blocks' ? (
            // Cards/Blocks View
            <div>
              {sortedGroups.map(([date, dateBookings]) => (
                <div key={date} style={{ marginBottom: '32px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '16px',
                    padding: '12px 16px',
                    backgroundColor: '#F5F3EB',
                    borderRadius: '8px',
                    border: '1px solid #E3DFD3'
                  }}>
                    <h4 style={{ 
                      margin: '0', 
                      fontSize: '18px', 
                      fontWeight: '700',
                      color: '#24332A'
                    }}>
                      {date === 'no-date' ? 'No Check-in Date' : format(new Date(date), 'EEEE, MMMM dd, yyyy')}
                    </h4>
                    <span style={{
                      backgroundColor: '#1A3E2A',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {dateBookings.length} {dateBookings.length === 1 ? 'booking' : 'bookings'}
                    </span>
                  </div>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', 
                    gap: '20px' 
                  }}>
                    {dateBookings.map(booking => (
                      <BookingCard
                        key={booking.id}
                        booking={booking}
                        onConfirm={onConfirm}
                        onCancel={onCancel}
                        onCheckIn={onCheckIn}
                        onCheckOut={onCheckOut}
                        onEditBooking={openEditBookingModal}
                        onEditPaymentStatus={startEditingPaymentStatus}
                        onEditBookingStatus={startEditingBookingStatus}
                        onDownloadInvoice={handleDownloadInvoice}
                        downloadingInvoice={downloadingInvoice}
                        editingPaymentStatus={editingPaymentStatus}
                        editingBookingStatus={editingBookingStatus}
                        newPaymentStatus={newPaymentStatus}
                        newBookingStatus={newBookingStatus}
                        setNewPaymentStatus={setNewPaymentStatus}
                        setNewBookingStatus={setNewBookingStatus}
                        handlePaymentStatusChange={handlePaymentStatusChange}
                        saveBookingStatus={saveBookingStatus}
                        cancelEditingPaymentStatus={cancelEditingPaymentStatus}
                        cancelEditingBookingStatus={cancelEditingBookingStatus}
                        getStatusBadge={getStatusBadge}
                        getPaymentStatusBadge={getPaymentStatusBadge}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : viewMode === 'calendar' ? (
            // Calendar View
            <CalendarView
              bookings={filteredBookings}
              currentDate={currentCalendarDate}
              onDateChange={setCurrentCalendarDate}
              onBookingClick={openEditBookingModal}
              getStatusBadge={getStatusBadge}
            />
          ) : (
            // List View - Enhanced Table
            <div style={{ backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
              {sortedGroups.map(([date, dateBookings], groupIndex) => (
                <div key={date}>
                  {/* Date Header */}
                  <div style={{
                    backgroundColor: '#F5F3EB',
                    padding: '12px 20px',
                    borderBottom: '1px solid #E3DFD3',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <h4 style={{ 
                      margin: '0', 
                      fontSize: '16px', 
                      fontWeight: '700',
                      color: '#24332A'
                    }}>
                      {date === 'no-date' ? 'No Check-in Date' : format(new Date(date), 'EEEE, MMMM dd, yyyy')}
                    </h4>
                    <span style={{
                      backgroundColor: '#1A3E2A',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {dateBookings.length}
                    </span>
                  </div>

                  {/* Table for this date group */}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F5F3EB' }}>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>Guest</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>Accommodation</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>Check-out</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>Status</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>Payment</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>Total</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dateBookings.map((booking, index) => (
                          <BookingTableRow
                            key={booking.id}
                            booking={booking}
                            isLast={index === dateBookings.length - 1 && groupIndex === sortedGroups.length - 1}
                            onConfirm={onConfirm}
                            onCancel={onCancel}
                            onCheckIn={onCheckIn}
                            onCheckOut={onCheckOut}
                            onEditBooking={openEditBookingModal}
                            onEditPaymentStatus={startEditingPaymentStatus}
                            onEditBookingStatus={startEditingBookingStatus}
                            onDownloadInvoice={handleDownloadInvoice}
                            downloadingInvoice={downloadingInvoice}
                            editingPaymentStatus={editingPaymentStatus}
                            editingBookingStatus={editingBookingStatus}
                            newPaymentStatus={newPaymentStatus}
                            newBookingStatus={newBookingStatus}
                            setNewPaymentStatus={setNewPaymentStatus}
                            setNewBookingStatus={setNewBookingStatus}
                            handlePaymentStatusChange={handlePaymentStatusChange}
                            saveBookingStatus={saveBookingStatus}
                            cancelEditingPaymentStatus={cancelEditingPaymentStatus}
                            cancelEditingBookingStatus={cancelEditingBookingStatus}
                            getStatusBadge={getStatusBadge}
                            getPaymentStatusBadge={getPaymentStatusBadge}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Edit Booking Modal */}
      {showEditModal && editingBooking && (
        <EditBookingModal
          booking={editingBooking}
          user={user}
          onClose={closeEditBookingModal}
          onUpdate={handleBookingUpdate}
          canCheckIn={canCheckIn}
          canCheckOut={canCheckOut}
        />
      )}

      {/* Invoice Selection Modal */}
      {showInvoiceModal && selectedBookingForInvoice && (
        <InvoiceSelectionModal
          isOpen={showInvoiceModal}
          onClose={handleCloseInvoiceModal}
          booking={selectedBookingForInvoice}
          onDownloadStandard={handleStandardInvoiceDownload}
          onDownloadFlexible={handleFlexibleInvoiceDownload}
          downloading={!!downloadingInvoice}
        />
      )}
    </div>
  );
}

// Booking Card Component for Cards View
function BookingCard({ 
  booking, 
  onConfirm, 
  onCancel, 
  onCheckIn, 
  onCheckOut, 
  onEditBooking,
  onEditPaymentStatus,
  onEditBookingStatus,
  onDownloadInvoice,
  downloadingInvoice,
  editingPaymentStatus,
  editingBookingStatus,
  newPaymentStatus,
  newBookingStatus,
  setNewPaymentStatus,
  setNewBookingStatus,
  handlePaymentStatusChange,
  saveBookingStatus,
  cancelEditingPaymentStatus,
  cancelEditingBookingStatus,
  getStatusBadge,
  getPaymentStatusBadge
}) {
  // Check if booking is from BOOKING_ENGINE
  const isBookingEngine = booking.bookingPlatform === 'BOOKING_ENGINE';
  
  return (
    <div style={{
      backgroundColor: isBookingEngine ? '#E1E9E2' : 'white', // Light blue shade for booking engine bookings
      border: isBookingEngine ? '1px solid #9DB7A5' : '1px solid #E3DFD3',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
            {booking.user?.name || 'N/A'}
          </h4>
          <p style={{ margin: '0', fontSize: '14px', color: '#5F6B61' }}>
            {booking.user?.email || 'N/A'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {editingBookingStatus === booking.id ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <select
                value={newBookingStatus}
                onChange={(e) => setNewBookingStatus(e.target.value)}
                style={{
                  padding: '6px 8px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white'
                }}
              >
                <option value="PENDING">PENDING</option>
                <option value="CONFIRMED">CONFIRMED</option>
                <option value="CHECKEDIN">CHECKED IN</option>
                <option value="CHECKEDOUT">CHECKED OUT</option>
                <option value="COMPLETED">COMPLETED</option>
                <option value="CANCELLED">CANCELLED</option>
              </select>
              <button
                onClick={() => saveBookingStatus(booking.id)}
                style={{
                  backgroundColor: '#1A3E2A',
                  color: 'white',
                  border: 'none',
                  padding: '6px 8px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                ✓
              </button>
              <button
                onClick={cancelEditingBookingStatus}
                style={{
                  backgroundColor: '#5F6B61',
                  color: 'white',
                  border: 'none',
                  padding: '6px 8px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {getStatusBadge(booking.status)}
              <button
                onClick={() => onEditBookingStatus(booking.id, booking.status)}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  padding: '6px 8px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  color: '#5F6B61'
                }}
              >
                <PencilIcon size="1em" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Accommodation Details */}
      <div style={{ 
        backgroundColor: '#F5F3EB', 
        padding: '16px', 
        borderRadius: '12px', 
        marginBottom: '16px',
        border: '1px solid #E3DFD3'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '20px' }}><HomeIcon size="1em" aria-hidden="true" /></span>
          <h5 style={{ margin: '0', fontSize: '16px', fontWeight: '600', color: '#24332A' }}>
            {getAccommodationDisplay(booking)}
          </h5>
        </div>
        
        {/* Show all rooms if multiple */}
        {(() => {
          const rooms = getAllRooms(booking);
          const beds = getAllBeds(booking);
          
          if (rooms.length > 1 || beds.length > 1) {
            return (
              <div style={{ margin: '8px 0 8px 28px', fontSize: '13px', color: '#5F6B61' }}>
                {rooms.map((room, idx) => (
                  <div key={room.bookedRoomId || idx} style={{ marginBottom: '4px' }}>
                    • Room {room.roomNumber} ({room.roomType?.name || 'N/A'}) - {room.nights} nights
                  </div>
                ))}
                {beds.map((bed, idx) => (
                  <div key={bed.bookedBedId || idx} style={{ marginBottom: '4px' }}>
                    • Bed {bed.bedNumber} in Room {bed.roomNumber} - {bed.nights} nights
                  </div>
                ))}
              </div>
            );
          }
          
          const room = rooms[0];
          const bed = beds[0];
          return (
            <p style={{ margin: '0 0 8px 28px', fontSize: '14px', color: '#5F6B61' }}>
              {room?.roomType?.name || bed?.roomType?.name || 'N/A'}
            </p>
          );
        })()}
        
        {(() => {
          const courseCount = getCourseCount(booking);
          const guestCount = getGuestCount(booking);
          
          if (courseCount > 1 || guestCount > 1) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '8px 0 0 28px' }}>
                {guestCount > 1 && (
                  <span style={{ fontSize: '13px', color: '#5F6B61' }}>
                    Guests: {guestCount}
                  </span>
                )}
                {courseCount > 1 && (
                  <span style={{ fontSize: '13px', color: '#5F6B61' }}>
                    Courses: {courseCount}
                  </span>
                )}
              </div>
            );
          }
          return null;
        })()}
        
        {(() => {
          const course = getPrimaryCourse(booking);
          if (course && course.fee > 0) {
            return (
              <p style={{ margin: '8px 0 0 28px', fontSize: '13px', color: '#5F6B61' }}>
                Course fee: ₹{course.fee}
              </p>
            );
          }
          return null;
        })()}
      </div>

      {/* Dates */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
            Check-in
          </label>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
            {booking.checkIn ? format(new Date(booking.checkIn), 'MMM dd, yyyy') : 'N/A'}
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
            Check-out
          </label>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
            {booking.checkOut ? format(new Date(booking.checkOut), 'MMM dd, yyyy') : 'N/A'}
          </div>
        </div>
      </div>

      {/* Payment Status */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '8px' }}>
          Payment Status
        </label>
        {editingPaymentStatus === booking.id ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={newPaymentStatus}
              onChange={(e) => setNewPaymentStatus(e.target.value)}
              style={{
                padding: '6px 8px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '12px',
                flex: 1,
                backgroundColor: 'white'
              }}
            >
              <option value="PENDING">PENDING</option>
              <option value="SUCCESS">SUCCESS</option>
              <option value="FAILED">FAILED</option>
              <option value="REFUNDED">REFUNDED</option>
            </select>
            <button
              onClick={() => handlePaymentStatusChange(booking.id, newPaymentStatus)}
              style={{
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '6px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              ✓
            </button>
            <button
              onClick={cancelEditingPaymentStatus}
              style={{
                backgroundColor: '#A13F2F',
                color: 'white',
                border: 'none',
                padding: '6px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>
        ) : (
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer'
            }}
            onClick={() => onEditPaymentStatus(booking.id, booking.paymentStatus || 'PENDING')}
            title="Click to edit payment status"
          >
            {getPaymentStatusBadge(booking.paymentStatus || 'PENDING')}
            <span style={{ fontSize: '12px', color: '#5F6B61' }}><PencilIcon size="1em" aria-hidden="true" /></span>
          </div>
        )}
      </div>

      {/* Total Price */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        padding: '12px 16px',
        backgroundColor: '#F0F3EE',
        borderRadius: '8px',
        border: '1px solid #1A3E2A'
      }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#122E20' }}>Total Amount</span>
        <span style={{ fontSize: '18px', fontWeight: '700', color: '#122E20' }}>₹{booking.totalPrice || 0}</span>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          onClick={() => onEditBooking(booking)}
          style={{
            backgroundColor: '#1A3E2A',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            flex: '1'
          }}
        >
          Edit
        </button>
        
        <button
          onClick={() => onDownloadInvoice(booking.id)}
          disabled={downloadingInvoice === booking.id}
          style={{
            backgroundColor: downloadingInvoice === booking.id ? '#A5A89B' : '#163626',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: downloadingInvoice === booking.id ? 'not-allowed' : 'pointer',
            flex: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
          title={downloadingInvoice === booking.id ? 'Downloading...' : 'Download Invoice'}
        >
          {downloadingInvoice === booking.id ? (
            <>
              <span style={{ 
                display: 'inline-block',
                width: '12px',
                height: '12px',
                border: '2px solid #ffffff40',
                borderTop: '2px solid #ffffff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></span>
              Downloading...
            </>
          ) : (
            <>Invoice</>
          )}
        </button>
        
        {booking.status === 'PENDING' && (
          <button
            onClick={() => onConfirm(booking.id)}
            style={{
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              flex: '1'
            }}
          >
            ✓ Confirm
          </button>
        )}
        
        {booking.status === 'CONFIRMED' && (
          <button
            onClick={() => onCheckIn(booking.id)}
            style={{
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              flex: '1'
            }}
          >
            Check In
          </button>
        )}
        
        {booking.status === 'CHECKEDIN' && (
          <button
            onClick={() => onCheckOut(booking.id)}
            style={{
              backgroundColor: '#5F6B61',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              flex: '1'
            }}
          >
            Check Out
          </button>
        )}
        
        {booking.status !== 'CANCELLED' && booking.status !== 'COMPLETED' && booking.status !== 'CHECKEDOUT' && (
          <button
            onClick={() => onCancel(booking.id)}
            style={{
              backgroundColor: '#A13F2F',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              flex: '1'
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

// Booking Table Row Component for List View
function BookingTableRow({ 
  booking, 
  isLast,
  onConfirm, 
  onCancel, 
  onCheckIn, 
  onCheckOut, 
  onEditBooking,
  onEditPaymentStatus,
  onEditBookingStatus,
  onDownloadInvoice,
  downloadingInvoice,
  editingPaymentStatus,
  editingBookingStatus,
  newPaymentStatus,
  newBookingStatus,
  setNewPaymentStatus,
  setNewBookingStatus,
  handlePaymentStatusChange,
  saveBookingStatus,
  cancelEditingPaymentStatus,
  cancelEditingBookingStatus,
  getStatusBadge,
  getPaymentStatusBadge
}) {
  // Check if booking is from BOOKING_ENGINE
  const isBookingEngine = booking.bookingPlatform === 'BOOKING_ENGINE';
  const defaultBgColor = isBookingEngine ? '#E1E9E2' : 'white'; // Light blue shade for booking engine bookings
  const hoverBgColor = isBookingEngine ? '#C4D4C8' : '#F5F3EB';
  
  return (
    <tr style={{ 
      backgroundColor: defaultBgColor,
      borderBottom: isLast ? 'none' : '1px solid #E3DFD3',
      transition: 'background-color 0.2s ease'
    }}
    onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = hoverBgColor}
    onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = defaultBgColor}
    >
      <td style={{ padding: '16px' }}>
        <div>
          <div style={{ fontWeight: '600', fontSize: '14px', color: '#1A3E2A' }}>{booking.user?.name}</div>
          <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '2px' }}>
            {booking.user?.email}
          </div>
        </div>
      </td>
      <td style={{ padding: '16px' }}>
        <div>
          <div style={{ fontWeight: '600', fontSize: '14px', color: '#1A3E2A' }}>
            {getAccommodationDisplay(booking)}
          </div>
          <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '2px' }}>
            {(() => {
              const rooms = getAllRooms(booking);
              const beds = getAllBeds(booking);
              const room = rooms[0];
              const bed = beds[0];
              return room?.roomType?.name || bed?.roomType?.name || 'N/A';
            })()}
          </div>
          {(() => {
            const courseCount = getCourseCount(booking);
            const guestCount = getGuestCount(booking);
            
            if (courseCount > 1 || guestCount > 1) {
              return (
                <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#5F6B61', marginTop: '4px' }}>
                  {guestCount > 1 && <span>Guests: {guestCount}</span>}
                  {courseCount > 1 && <span>Courses: {courseCount}</span>}
                </div>
              );
            }
            return null;
          })()}
          {(() => {
            const course = getPrimaryCourse(booking);
            if (course && course.fee > 0) {
              return (
                <div style={{ fontSize: '11px', color: '#5F6B61', marginTop: '2px' }}>
                  Course fee: ₹{course.fee}
                </div>
              );
            }
            return null;
          })()}
        </div>
      </td>
      <td style={{ padding: '16px', fontSize: '14px', color: '#1A3E2A' }}>
        {booking.checkOut ? format(new Date(booking.checkOut), 'MMM dd, yyyy') : 'N/A'}
      </td>
      <td style={{ padding: '16px' }}>
        {editingBookingStatus === booking.id ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={newBookingStatus}
              onChange={(e) => setNewBookingStatus(e.target.value)}
              style={{
                padding: '6px 8px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '12px',
                backgroundColor: 'white'
              }}
            >
              <option value="PENDING">PENDING</option>
              <option value="CONFIRMED">CONFIRMED</option>
              <option value="CHECKEDIN">CHECKED IN</option>
              <option value="CHECKEDOUT">CHECKED OUT</option>
              <option value="COMPLETED">COMPLETED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>
            <button
              onClick={() => saveBookingStatus(booking.id)}
              style={{
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '6px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              ✓
            </button>
            <button
              onClick={cancelEditingBookingStatus}
              style={{
                backgroundColor: '#5F6B61',
                color: 'white',
                border: 'none',
                padding: '6px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {getStatusBadge(booking.status)}
            <button
              onClick={() => onEditBookingStatus(booking.id, booking.status)}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                padding: '4px 6px',
                fontSize: '12px',
                cursor: 'pointer',
                color: '#5F6B61'
              }}
            >
              <PencilIcon size="1em" aria-hidden="true" />
            </button>
          </div>
        )}
      </td>
      <td style={{ padding: '16px' }}>
        {editingPaymentStatus === booking.id ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={newPaymentStatus}
              onChange={(e) => setNewPaymentStatus(e.target.value)}
              style={{
                padding: '6px 8px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '12px',
                backgroundColor: 'white'
              }}
            >
              <option value="PENDING">PENDING</option>
              <option value="SUCCESS">SUCCESS</option>
              <option value="FAILED">FAILED</option>
              <option value="REFUNDED">REFUNDED</option>
            </select>
            <button
              onClick={() => handlePaymentStatusChange(booking.id, newPaymentStatus)}
              style={{
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '6px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              ✓
            </button>
            <button
              onClick={cancelEditingPaymentStatus}
              style={{
                backgroundColor: '#A13F2F',
                color: 'white',
                border: 'none',
                padding: '6px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>
        ) : (
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer'
            }}
            onClick={() => onEditPaymentStatus(booking.id, booking.paymentStatus || 'PENDING')}
            title="Click to edit payment status"
          >
            {getPaymentStatusBadge(booking.paymentStatus || 'PENDING')}
            <span style={{ fontSize: '12px', color: '#5F6B61' }}><PencilIcon size="1em" aria-hidden="true" /></span>
          </div>
        )}
      </td>
      <td style={{ padding: '16px', fontSize: '16px', fontWeight: '600', color: '#1A3E2A' }}>
        ₹{booking.totalPrice || 0}
      </td>
      <td style={{ padding: '16px' }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button
            onClick={() => onEditBooking(booking)}
            style={{
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Edit
          </button>
          <button
            onClick={() => onDownloadInvoice(booking.id)}
            disabled={downloadingInvoice === booking.id}
            style={{
              backgroundColor: downloadingInvoice === booking.id ? '#A5A89B' : '#163626',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: downloadingInvoice === booking.id ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              minWidth: '80px'
            }}
            title={downloadingInvoice === booking.id ? 'Downloading...' : 'Download Invoice'}
          >
            {downloadingInvoice === booking.id ? (
              <>
                <span style={{ 
                  display: 'inline-block',
                  width: '10px',
                  height: '10px',
                  border: '2px solid #ffffff40',
                  borderTop: '2px solid #ffffff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></span>
                Loading...
              </>
            ) : (
              <>Invoice</>
            )}
          </button>
          {booking.status === 'PENDING' && (
            <button
              onClick={() => onConfirm(booking.id)}
              style={{
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Confirm
            </button>
          )}
          {booking.status === 'CONFIRMED' && (
            <button
              onClick={() => onCheckIn(booking.id)}
              style={{
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Check In
            </button>
          )}
          {booking.status === 'CHECKEDIN' && (
            <button
              onClick={() => onCheckOut(booking.id)}
              style={{
                backgroundColor: '#5F6B61',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Check Out
            </button>
          )}
          {booking.status !== 'CANCELLED' && booking.status !== 'COMPLETED' && booking.status !== 'CHECKEDOUT' && (
            <button
              onClick={() => onCancel(booking.id)}
              style={{
                backgroundColor: '#A13F2F',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// Audit Tab Component
function AuditTab({ auditLogs }) {
  const [selectedLog, setSelectedLog] = useState(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);

  const getActionIcon = (action) => {
    const icons = {
      'BOOKING_CREATED': '',
      'BOOKING_UPDATED': '',
      'BOOKING_CANCELLED': '',
      'BOOKING_CONFIRMED': '',
      'BOOKING_CHECKEDIN': '',
      'BOOKING_CHECKEDOUT': '',
      'USER_CREATED': '',
      'USER_UPDATED': '',
      'USER_LOGIN': '',
      'USER_LOGOUT': '',
      'ROOM_CREATED': '',
      'ROOM_UPDATED': '',
      'CREATE': '',
      'UPDATE': '',
      'DELETE': '',
      'LOGIN': '',
      'LOGOUT': ''
    };
    return icons[action] || '';
  };

  const formatMetadataPreview = (metadata) => {
    if (!metadata) return 'N/A';
    
    try {
      const data = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
      const details = [];
      
      if (data.bookingId) details.push(`Booking: ...${data.bookingId.slice(-8)}`);
      if (data.roomId) details.push(`Room: ...${data.roomId.slice(-8)}`);
      if (data.bedId) details.push(`Bed: ...${data.bedId.slice(-8)}`);
      if (data.userId) details.push(`User: ...${data.userId.slice(-8)}`);
      
      const previewText = details.length > 0 ? details.slice(0, 2).join(', ') : 'System action';
      const hasMore = details.length > 2 || Object.keys(data).length > 4;
      
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{previewText}</span>
          {hasMore && <span style={{ color: '#1A3E2A', fontSize: '12px' }}>+more</span>}
        </div>
      );
    } catch (e) {
      const metaString = String(metadata);
      return metaString.substring(0, 50) + (metaString.length > 50 ? '...' : '');
    }
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowMetadataModal(true);
  };

  const handleCloseModal = () => {
    setShowMetadataModal(false);
    setSelectedLog(null);
  };

  return (
    <div>
      <h3>Audit Logs</h3>
      {auditLogs.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          color: '#5F6B61'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}><BarChart3Icon size="1em" aria-hidden="true" /></div>
          <p>No audit logs found.</p>
          <p style={{ fontSize: '14px' }}>
            Audit logs will appear here when users perform actions like creating bookings, updating rooms, etc.
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #E3DFD3' }}>
                <th style={{ padding: '10px', textAlign: 'left' }}>Action</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>User</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Details</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map(log => (
                <tr key={log.id} style={{ borderBottom: '1px solid #E3DFD3' }}>
                  <td style={{ padding: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{getActionIcon(log.action)}</span>
                      <span style={{ fontWeight: 'bold' }}>{log.action}</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px' }}>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>
                        {log.user?.name || 'System'}
                      </div>
                      {log.user?.email && (
                        <div style={{ fontSize: '12px', color: '#5F6B61' }}>
                          {log.user.email}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '10px' }}>
                    <div style={{ maxWidth: '300px', fontSize: '14px' }}>
                      {formatMetadataPreview(log.metadata)}
                    </div>
                    <button
                      onClick={() => handleViewDetails(log)}
                      style={{
                        marginTop: '8px',
                        padding: '4px 12px',
                        backgroundColor: '#1A3E2A',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      View Full Details
                    </button>
                  </td>
                  <td style={{ padding: '10px' }}>
                    <div style={{ fontSize: '14px' }}>
                      {log.createdAt ? format(new Date(log.createdAt), 'MMM dd, yyyy HH:mm') : 'N/A'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Metadata Details Modal */}
      {showMetadataModal && selectedLog && (
        <AuditMetadataModal
          log={selectedLog}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

// Audit Metadata Modal Component
function AuditMetadataModal({ log, onClose }) {
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  const toggleKey = (key) => {
    const newExpanded = new Set(expandedKeys);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedKeys(newExpanded);
  };

  const parseMetadata = () => {
    if (!log.metadata) return null;
    
    try {
      return typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
    } catch (e) {
      return { raw: String(log.metadata) };
    }
  };

  const renderValue = (value, parentKey = '') => {
    if (value === null || value === undefined) {
      return <span style={{ color: '#A5A89B', fontStyle: 'italic' }}>null</span>;
    }

    if (typeof value === 'boolean') {
      return <span style={{ color: value ? '#1A3E2A' : '#A13F2F', fontWeight: 'bold' }}>{String(value)}</span>;
    }

    if (typeof value === 'number') {
      return <span style={{ color: '#1A3E2A', fontWeight: '500' }}>{value}</span>;
    }

    if (typeof value === 'string') {
      // Check if it looks like a date
      if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
        try {
          return (
            <span style={{ color: '#A9613C' }}>
              {format(new Date(value), 'MMM dd, yyyy HH:mm:ss')}
            </span>
          );
        } catch (e) {
          return <span style={{ color: '#1A3E2A' }}>{value}</span>;
        }
      }
      return <span style={{ color: '#1A3E2A' }}>{value}</span>;
    }

    if (Array.isArray(value)) {
      const key = parentKey;
      const isExpanded = expandedKeys.has(key);
      
      return (
        <div>
          <button
            onClick={() => toggleKey(key)}
            style={{
              background: 'none',
              border: 'none',
              color: '#1A3E2A',
              cursor: 'pointer',
              padding: '0',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {isExpanded ? '▼' : '▶'} Array [{value.length}]
          </button>
          {isExpanded && (
            <div style={{ marginLeft: '20px', marginTop: '8px', borderLeft: '2px solid #E3DFD3', paddingLeft: '12px' }}>
              {value.map((item, index) => (
                <div key={index} style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#5F6B61', fontSize: '12px' }}>[{index}]</span>
                  <div style={{ marginLeft: '12px' }}>
                    {renderValue(item, `${key}[${index}]`)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (typeof value === 'object') {
      const key = parentKey;
      const isExpanded = expandedKeys.has(key);
      const keys = Object.keys(value);
      
      return (
        <div>
          <button
            onClick={() => toggleKey(key)}
            style={{
              background: 'none',
              border: 'none',
              color: '#1A3E2A',
              cursor: 'pointer',
              padding: '0',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {isExpanded ? '▼' : '▶'} Object {'{'}...{'}'}
          </button>
          {isExpanded && (
            <div style={{ marginLeft: '20px', marginTop: '8px', borderLeft: '2px solid #E3DFD3', paddingLeft: '12px' }}>
              {keys.map(k => (
                <div key={k} style={{ marginBottom: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span style={{ color: '#163626', fontWeight: '600', fontSize: '13px' }}>{k}:</span>
                    <div style={{ flex: 1 }}>
                      {renderValue(value[k], `${key}.${k}`)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  const metadata = parseMetadata();
  const metadataKeys = metadata ? Object.keys(metadata) : [];

  const copyToClipboard = () => {
    const text = JSON.stringify(metadata, null, 2);
    navigator.clipboard.writeText(text);
    toast.success('Metadata copied to clipboard!');
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#E9DFCE',
        borderRadius: '12px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '2px solid #E3DFD3',
          backgroundColor: '#F5F3EB'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div style={{ flex: 1 }}>
              <h2 style={{ margin: '0 0 12px 0', color: '#1A3E2A', fontSize: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span><ClipboardListIcon size="1em" aria-hidden="true" /></span> Audit Log Details
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                <div>
                  <span style={{ color: '#5F6B61', fontWeight: '500' }}>Action:</span>
                  <span style={{ marginLeft: '8px', fontWeight: 'bold', color: '#1A3E2A' }}>{log.action}</span>
                </div>
                <div>
                  <span style={{ color: '#5F6B61', fontWeight: '500' }}>User:</span>
                  <span style={{ marginLeft: '8px', fontWeight: 'bold', color: '#1A3E2A' }}>{log.user?.name || 'System'}</span>
                </div>
                <div>
                  <span style={{ color: '#5F6B61', fontWeight: '500' }}>Timestamp:</span>
                  <span style={{ marginLeft: '8px', color: '#1A3E2A' }}>
                    {log.createdAt ? format(new Date(log.createdAt), 'MMM dd, yyyy HH:mm:ss') : 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ color: '#5F6B61', fontWeight: '500' }}>Log ID:</span>
                  <span style={{ marginLeft: '8px', fontFamily: 'monospace', fontSize: '12px', color: '#5F6B61' }}>
                    {log.id}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#5F6B61',
                padding: '0 8px',
                marginLeft: '16px'
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{
          padding: '24px',
          overflowY: 'auto',
          flex: 1,
          backgroundColor: '#ffffff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: '#1A3E2A', fontSize: '18px' }}>
              Metadata {metadataKeys.length > 0 && `(${metadataKeys.length} fields)`}
            </h3>
            <button
              onClick={copyToClipboard}
              style={{
                padding: '8px 16px',
                backgroundColor: '#EEEBE1',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                color: '#36433B',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              Copy JSON
            </button>
          </div>

          {!metadata || metadataKeys.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#F5F3EB',
              borderRadius: '8px',
              color: '#5F6B61'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}><InboxIcon size="1em" aria-hidden="true" /></div>
              <p style={{ margin: 0 }}>No metadata available for this log entry</p>
            </div>
          ) : (
            <div style={{
              backgroundColor: '#F5F3EB',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid #E3DFD3',
              fontFamily: 'monospace',
              fontSize: '13px'
            }}>
              {metadataKeys.map(key => (
                <div key={key} style={{
                  marginBottom: '12px',
                  padding: '12px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #E3DFD3'
                }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'start' }}>
                    <span style={{
                      color: '#163626',
                      fontWeight: '700',
                      fontSize: '14px',
                      minWidth: 'fit-content'
                    }}>
                      {key}:
                    </span>
                    <div style={{ flex: 1, wordBreak: 'break-word' }}>
                      {renderValue(metadata[key], key)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #E3DFD3',
          backgroundColor: '#F5F3EB',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Night Audit Modal Component
function NightAuditModal({ 
  nightAuditStatus, 
  nightAuditLoading, 
  onClose, 
  onStartNightAudit, 
  onCompleteNightAudit, 
  onReopenNightAudit, 
  user 
}) {
  const todayDate = format(new Date(), 'EEEE, MMMM dd, yyyy');
  const isCompleted = nightAuditStatus?.status === 'COMPLETED';
  const canReopen = user.role === 'SUPERADMIN' && isCompleted;

  const handleCompleteAudit = () => {
    onCompleteNightAudit();
    // Close modal after a short delay to show completion
    setTimeout(() => onClose(), 1500);
  };

  const handleReopenAudit = () => {
    onReopenNightAudit();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#E9DFCE',
        borderRadius: '16px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 24px 0 24px',
          borderBottom: '1px solid #E3DFD3',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700', color: '#1A3E2A' }}>
                Night Audit
              </h3>
              <p style={{ margin: '0 0 16px 0', color: '#5F6B61', fontSize: '16px' }}>
                {todayDate}
              </p>
              
              {/* Status Badge */}
              <div style={{ marginBottom: '16px' }}>
                {!nightAuditStatus ? (
                  <span style={{
                    backgroundColor: '#F3E6CB',
                    color: '#684A1F',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '600',
                    border: '1px solid #C9903C'
                  }}>
                    Not Started
                  </span>
                ) : (
                  <span style={{
                    backgroundColor: isCompleted ? '#E1E9E2' : '#F3E6CB',
                    color: isCompleted ? '#0F2619' : '#684A1F',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '600',
                    border: `1px solid ${isCompleted ? '#1A3E2A' : '#C9903C'}`
                  }}>
                    {isCompleted ? 'Completed' : 'In Progress'}
                  </span>
                )}
              </div>
            </div>
            
            <button
              onClick={onClose}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#5F6B61',
                padding: '4px'
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '0 24px 24px 24px' }}>
          {!nightAuditStatus ? (
            // Start Night Audit Section
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}><MoonIcon size="1em" aria-hidden="true" /></div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1A3E2A' }}>
                Start Night Audit for Today
              </h4>
              <p style={{ margin: '0 0 24px 0', color: '#5F6B61', fontSize: '14px' }}>
                Begin the night audit process to manage end-of-day operations and prepare for the next business day.
              </p>
              <button
                onClick={onStartNightAudit}
                disabled={nightAuditLoading}
                style={{
                  backgroundColor: '#1A3E2A',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: nightAuditLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: '0 auto'
                }}
              >
                {nightAuditLoading ? 'Starting...' : 'Start Night Audit'}
              </button>
            </div>
          ) : (
            // Simple Night Audit Status Section
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>
                {isCompleted ? '' : '�'}
              </div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1A3E2A' }}>
                {isCompleted ? 'Night Audit Completed' : 'Night Audit In Progress'}
              </h4>
              <p style={{ margin: '0 0 24px 0', color: '#5F6B61', fontSize: '14px' }}>
                {isCompleted 
                  ? 'All operations for this day are locked. Only super admin can reopen.' 
                  : 'Night audit is active. Complete when all end-of-day tasks are finished.'}
              </p>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                {!isCompleted && (
                  <button
                    onClick={handleCompleteAudit}
                    disabled={nightAuditLoading}
                    style={{
                      backgroundColor: '#1A3E2A',
                      color: 'white',
                      border: 'none',
                      padding: '14px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: nightAuditLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {nightAuditLoading ? 'Completing...' : 'Complete Night Audit'}
                  </button>
                )}
                
                {canReopen && (
                  <button
                    onClick={handleReopenAudit}
                    disabled={nightAuditLoading}
                    style={{
                      backgroundColor: '#B68235',
                      color: 'white',
                      border: 'none',
                      padding: '14px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: nightAuditLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {nightAuditLoading ? 'Reopening...' : 'Reopen Audit'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Audit Details */}
          {nightAuditStatus && (
            <div style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: '#F5F3EB',
              borderRadius: '8px',
              border: '1px solid #E3DFD3'
            }}>
              <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                Audit Information
              </h5>
              <div style={{ fontSize: '13px', color: '#5F6B61', lineHeight: '1.5' }}>
                <div><strong>Started:</strong> {nightAuditStatus.startedAt ? format(new Date(nightAuditStatus.startedAt), 'MMM dd, yyyy HH:mm') : 'N/A'}</div>
                {nightAuditStatus.completedAt && (
                  <div><strong>Completed:</strong> {format(new Date(nightAuditStatus.completedAt), 'MMM dd, yyyy HH:mm')}</div>
                )}
                <div><strong>Started By:</strong> {nightAuditStatus.startedBy || 'System'}</div>
                {nightAuditStatus.completedBy && (
                  <div><strong>Completed By:</strong> {nightAuditStatus.completedBy}</div>
                )}
                {nightAuditStatus.notes && (
                  <div style={{ marginTop: '8px' }}>
                    <strong>Notes:</strong> {nightAuditStatus.notes}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
